import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('pfc_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('pfc_token');
      localStorage.removeItem('pfc_user');
      if (window.location.pathname !== '/login' && window.location.pathname !== '/landing' && window.location.pathname !== '/register') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  register: (d) => api.post('/api/auth/register', d),
  login: (d) => api.post('/api/auth/login', d),
  getMe: () => api.get('/api/auth/me'),
  updateProfile: (d) => api.put('/api/auth/profile', d),
  googleSession: (sessionId) => api.post(`/api/auth/google/session?session_id=${sessionId}`),
  logout: () => api.post('/api/auth/logout'),
};

export const dataAPI = {
  getLeagues: () => api.get('/api/leagues'),
  getSeasons: (lid) => api.get('/api/seasons', { params: { league_id: lid } }),
  getTeams: (sid) => api.get(`/api/seasons/${sid}/teams`),
  getAllTeams: () => api.get('/api/teams'),
};

export const poolAPI = {
  list: (p) => api.get('/api/pools', { params: p }),
  get: (id) => api.get(`/api/pools/${id}`),
  getByInvite: (c) => api.get(`/api/pools/invite/${c}`),
  create: (d) => api.post('/api/pools', d),
  update: (id, d) => api.put(`/api/pools/${id}`, d),
  join: (id, alias, pin) => api.post(`/api/pools/${id}/join?team_alias=${encodeURIComponent(alias || '')}${pin ? `&pin=${pin}` : ''}`),
  updateAlias: (id, alias) => api.put(`/api/pools/${id}/alias?team_alias=${encodeURIComponent(alias)}`),
  upgrade: (id, plan) => api.put(`/api/pools/${id}/upgrade?plan=${plan}`),
  getPlans: () => api.get('/api/plans'),
};

// Payments (removed - organizer subscription model)
// Entry fees are no longer collected through the platform

export const entryAPI = {
  create: (poolId, numEntries) => api.post(`/api/pools/${poolId}/entries`, { num_entries: numEntries }),
  submitPicks: (entryId, d) => api.put(`/api/entries/${entryId}/picks`, d),
  myEntries: (poolId) => api.get(`/api/pools/${poolId}/my-entries`),
  list: (poolId) => api.get(`/api/pools/${poolId}/entries`),
  weeklyBreakdown: (entryId) => api.get(`/api/entries/${entryId}/weekly-breakdown`),
  remove: (poolId, entryId) => api.delete(`/api/pools/${poolId}/entries/${entryId}`),
  importCSV: (poolId, file) => { const f = new FormData(); f.append('file', file); return api.post(`/api/pools/${poolId}/import-entries`, f, { headers: { 'Content-Type': 'multipart/form-data' } }); },
};

export const scoringAPI = {
  compute: (poolId) => api.post(`/api/pools/${poolId}/compute-standings`),
  leaderboard: (poolId, p) => api.get(`/api/pools/${poolId}/leaderboard`, { params: p }),
};

export const chatAPI = {
  list: (poolId, p) => api.get(`/api/pools/${poolId}/chat`, { params: p }),
  post: (poolId, message, type) => api.post(`/api/pools/${poolId}/chat`, { message, type: type || 'general' }),
  delete: (id) => api.delete(`/api/chat/${id}`),
  unread: (poolId) => api.get(`/api/pools/${poolId}/unread`),
};

export const adminAPI = {
  stats: () => api.get('/api/admin/stats'),
  pools: () => api.get('/api/admin/pools'),
  users: (p) => api.get('/api/admin/users', { params: p }),
  userDetail: (id) => api.get(`/api/admin/users/${id}/detail`),
  suspendUser: (id, s) => api.put(`/api/admin/users/${id}/suspend?suspended=${s}`),
  deleteUser: (id) => api.delete(`/api/admin/users/${id}`),
  changeRole: (id, r) => api.put(`/api/admin/users/${id}/role?role=${r}`),
  syncResults: () => api.post('/api/admin/sync-results'),
  syncStatus: () => api.get('/api/admin/sync-status'),
  apiStandings: () => api.get('/api/admin/api-standings'),
  setTeamTier: (id, t) => api.put(`/api/admin/teams/${id}/tier?tier=${t}`),
  revenue: () => api.get('/api/admin/revenue'),
  contacts: () => api.get('/api/admin/contacts'),
  toggleContactRead: (id, read) => api.put(`/api/admin/contacts/${id}/read?read=${read}`),
  overrideMatch: (id, d) => api.put(`/api/admin/matches/${id}/override`, d),
  getSettings: () => api.get('/api/admin/settings'),
  updateSettings: (d) => api.put('/api/admin/settings', d),
  lockAllPools: () => api.post('/api/admin/lock-all-pools'),
  unlockAllPools: () => api.post('/api/admin/unlock-all-pools'),
  deletePool: (id) => api.delete(`/api/admin/pools/${id}`),
};

export const dashboardAPI = {
  myDashboard: () => api.get('/api/my-dashboard'),
};

export const feeTrackingAPI = {
  get: (poolId) => api.get(`/api/pools/${poolId}/fee-tracking`),
  update: (poolId, userId, paid) => api.put(`/api/pools/${poolId}/fee-tracking/${userId}?paid=${paid}`),
};

export const contactAPI = {
  submit: (d) => api.post('/api/contact', d),
};

export const seasonAPI = {
  status: () => api.get('/api/season-status'),
};

export const matchAPI = {
  list: (p) => api.get('/api/matches', { params: p }),
  teamStats: (teamApiId) => api.get(`/api/teams/${teamApiId}/statistics`),
  topScorers: (season) => api.get('/api/players/top-scorers', { params: season ? { season } : {} }),
  topAssists: (season) => api.get('/api/players/top-assists', { params: season ? { season } : {} }),
  playerStats: (playerId) => api.get(`/api/players/${playerId}/statistics`),
};

export default api;
